import { inject, Injectable } from '@angular/core'

import { StorageService } from '@app/services/storage.service'

import type { ConversationProfile } from '../models/conversation-profile.model'
import type { Message } from '../models/message.model'
import type { MessagePhotoAlbumPhoto } from '../modules/message-photo/models/message-photo-album-photo.model'
import type { UnsentVoiceMessage } from '../modules/voice-message/models/voice-message.model'

const UNSENT_MESSAGES_STORAGE_KEY = 'unsent_messages'

interface UnsentMessage {
  body: string
  voice: UnsentVoiceMessage | undefined
}

@Injectable({ providedIn: 'root' })
export class MessageCacheService {
  private readonly storageService = inject(StorageService)

  private initialised = false
  private unsentMessages: Record<
    string,
    {
      body: string
      voice: UnsentVoiceMessage | undefined
    }
  > = {}

  async init(): Promise<void> {
    if (this.initialised) {
      return
    }

    this.initialised = true

    this.unsentMessages = await this.storageService.getWithFallback<
      Record<
        string,
        {
          body: string
          photos: MessagePhotoAlbumPhoto[]
          voice: UnsentVoiceMessage
        }
      >
    >(UNSENT_MESSAGES_STORAGE_KEY, {})
  }

  getCachedMessage(profile: ConversationProfile): UnsentMessage {
    const cachedMessage = this.unsentMessages[profile.username] ?? this.unsentMessages[profile.browse_id]
    if (cachedMessage !== undefined) {
      this.saveMessage(profile, cachedMessage)
    }
    return (
      cachedMessage ?? {
        body: '',
        voice: undefined,
      }
    )
  }

  removeCachedMessage(profile: ConversationProfile, messages: Message[]): void {
    const currentCachedMessage = this.getCachedMessage(profile)
    const newCachedMessage = {
      body: messages.some(message => message.type === 'text') ? '' : currentCachedMessage.body,
      voice: messages.some(message => message.type === 'voice') ? undefined : currentCachedMessage.voice,
    }
    this.unsentMessages[profile.browse_id] = newCachedMessage
    this.unsentMessages[profile.username] = newCachedMessage

    void this.storageService.safeSet(UNSENT_MESSAGES_STORAGE_KEY, this.unsentMessages)
  }

  saveCachedMessage(profile: ConversationProfile, message: Partial<UnsentMessage>): void {
    const currentCachedMessage = this.getCachedMessage(profile)
    const newCachedMessage = {
      body: currentCachedMessage.body,
      voice: currentCachedMessage.voice,
      ...message,
    }
    this.saveMessage(profile, newCachedMessage)
  }

  private saveMessage(profile: ConversationProfile, message: UnsentMessage): void {
    this.unsentMessages[profile.browse_id] = message
    this.unsentMessages[profile.username] = message
    void this.storageService.safeSet(UNSENT_MESSAGES_STORAGE_KEY, this.unsentMessages)
  }
}
